import { Component, HostListener, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { toSignal, toObservable } from '@angular/core/rxjs-interop';
import { switchMap, catchError, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { PreventionGroupHeaderComponent } from '../../component/prevention-group-header/prevention-group-header.component';
import {
  PreventionGroupService,
  OpportunityDashboardView,
  OpportunityRow,
  AGE_BANDS,
} from '../../core/services/prevention.service';

type OpportunitySortKey = 'uncoveredCount' | 'detectedSessions';

/** "À retenir" -- sélection de PRÉSENTATION sur des lignes déjà visibles (jamais un moteur de
 *  recommandation ni un calcul métier) : ligne visible ayant le plus grand `uncoveredCount` (départage
 *  par le plus faible `coverageRate` fourni par l'API), sinon un message positif basé sur le
 *  `coverageRate` GLOBAL fourni par l'API. Jamais affiché quand les totaux sont retenus. */
type OpportunityInsight =
  | { kind: 'topUncovered'; subjectLabel: string; subjectKey: string; uncoveredCount: number; uncoveredSiteCount: number }
  | { kind: 'goodCoverage'; rate: number };

/**
 * Groupe > Prévention > Opportunités de prévention (lot L6) -- consomme la lecture UNIFIÉE
 * {@link PreventionGroupService#getOpportunities} (opportunités cliniques et sujets catalogue
 * contextuels dans une seule liste, sans jamais montrer leur provenance).
 * <p>Le front RESTITUE STRICTEMENT le contrat backend : aucun recalcul de KPI/taux, aucune fusion de
 * sujets, aucune déduction de raison/action/origine. Points de doctrine :
 * <ul>
 *   <li>{@code subjectKey} est OPAQUE : renvoyée telle quelle à la navigation puis à l'API, jamais
 *       parsée, jamais reconstruite, jamais affichée ;</li>
 *   <li>{@code subjectOrigin} n'est ni affichée ni utilisée (ni badge, ni filtre) ;</li>
 *   <li>{@code totalsWithheld} : les KPI globaux concernés sont MASQUÉS -- jamais 0, jamais « — », jamais
 *       une valeur reconstruite depuis les lignes ;</li>
 *   <li>ligne {@code insufficientData} : « Données insuffisantes », aucun nombre.</li>
 * </ul>
 */
@Component({
  selector: 'app-prevention-besoins-couverture',
  standalone: true,
  imports: [TranslatePipe, PreventionGroupHeaderComponent],
  templateUrl: './prevention-besoins-couverture.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./prevention-besoins-couverture.component.scss'],
})
export class PreventionBesoinsCouvertureComponent {
  private readonly groupSvc = inject(PreventionGroupService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);

  readonly ageBands = AGE_BANDS;

  readonly periodDays = signal(30);
  readonly ageBand = signal<string | null>(null);
  readonly loadError = signal(false);
  readonly qualityPanelOpen = signal(false);

  private readonly trigger = computed(() => ({ days: this.periodDays(), ageBand: this.ageBand() }));

  readonly dashboard = toSignal(
    toObservable(this.trigger).pipe(
      tap(() => this.loadError.set(false)),
      switchMap(({ days, ageBand }) => this.groupSvc.getOpportunities(days, ageBand).pipe(
        catchError(() => { this.loadError.set(true); return of(null); })
      ))
    ),
    { initialValue: null as OpportunityDashboardView | null }
  );

  setPeriodDays(days: number): void {
    this.periodDays.set(days);
  }

  setAgeBand(value: string): void {
    this.ageBand.set(value === '' ? null : value);
  }

  toggleQualityPanel(): void {
    this.qualityPanelOpen.update(v => !v);
  }

  /** Ferme le panneau au clic en dehors -- clics à l'intérieur stoppés en template (stopPropagation). */
  @HostListener('document:click')
  closeQualityPanelOnOutsideClick(): void {
    if (this.qualityPanelOpen()) this.qualityPanelOpen.set(false);
  }

  /** Aucune session terminée sur la période (`completedSessions` est toujours fourni, jamais masqué) -- absence TOTALE de données, jamais confondue avec la petite cohorte. */
  readonly noDataAtAll = computed(() => this.dashboard()?.completedSessions === 0);

  /** Des sessions existent mais la cohorte globale reste sous le seuil : drapeau `globalCohortInsufficient` fourni par l'API (jamais recalculé ici). */
  readonly collectingNotYetSufficient = computed(() => !this.noDataAtAll() && this.dashboard()?.globalCohortInsufficient === true);

  /** Totaux retenus alors que la cohorte globale est suffisante (une ligne est masquée, etc.) : note explicative à côté des KPI. La cohorte insuffisante a déjà son propre bandeau. */
  readonly showTotalsWithheldNotice = computed(() => {
    const d = this.dashboard();
    return !!d && d.totalsWithheld && !this.noDataAtAll() && !d.globalCohortInsufficient;
  });

  /** KPI globaux affichables : uniquement quand l'API ne retient pas les totaux ET fournit la valeur (jamais 0 / « — » pour un `null`). */
  readonly opportunityCount = computed(() => this.visibleTotal(d => d.opportunityCount));
  readonly distinctSessions = computed(() => this.visibleTotal(d => d.distinctSessions));
  readonly uncoveredCount = computed(() => this.visibleTotal(d => d.uncoveredCount));

  /** Colonnes de la grille KPI (présentation pure) : « Parcours analysés » + les cartes réellement affichées, 2 minimum. */
  readonly kpiColumnCount = computed(() => {
    const visible = [this.opportunityCount(), this.distinctSessions(), this.uncoveredCount()].filter(v => v !== null).length;
    return Math.max(2, 1 + visible);
  });

  private visibleTotal(pick: (d: OpportunityDashboardView) => number | null): number | null {
    const d = this.dashboard();
    if (!d || d.totalsWithheld) return null;
    return pick(d);
  }

  readonly keyInsight = computed<OpportunityInsight | null>(() => {
    const d = this.dashboard();
    if (!d || this.noDataAtAll() || d.totalsWithheld) return null;
    const visible = d.opportunities.filter(o => !o.insufficientData);
    if (visible.length === 0) return null;

    const top = visible.reduce((best, o) => {
      const bestUncovered = best.uncoveredCount ?? 0;
      const oUncovered = o.uncoveredCount ?? 0;
      if (oUncovered !== bestUncovered) return oUncovered > bestUncovered ? o : best;
      const bestRate = best.coverageRate ?? 1;
      const oRate = o.coverageRate ?? 1;
      return oRate < bestRate ? o : best;
    });

    if ((top.uncoveredCount ?? 0) > 0) {
      return {
        kind: 'topUncovered', subjectLabel: top.subjectLabel, subjectKey: top.subjectKey,
        uncoveredCount: top.uncoveredCount!, uncoveredSiteCount: top.uncoveredSiteCount ?? 0,
      };
    }
    if (d.coverageRate != null) {
      return { kind: 'goodCoverage', rate: d.coverageRate };
    }
    return null;
  });

  // ───────────────────────── Tableau des opportunités ─────────────────────────

  readonly sort = signal<{ key: OpportunitySortKey; dir: 'asc' | 'desc' }>({ key: 'uncoveredCount', dir: 'desc' });

  /** Tri d'AFFICHAGE (l'API renvoie les lignes dans l'ordre de leur clé, le tri relève de l'UI). Les lignes masquées (valeur `null`) n'ont aucun nombre fiable à trier -- toujours en fin de liste, jamais assimilées à 0. */
  readonly sortedOpportunities = computed<OpportunityRow[]>(() => {
    const rows = this.dashboard()?.opportunities ?? [];
    const sort = this.sort();
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return (av - bv) * dir;
    });
  });

  toggleSort(key: OpportunitySortKey): void {
    const current = this.sort();
    this.sort.set(current.key === key ? { key, dir: current.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' });
  }

  /**
   * Détail d'un sujet -- même les lignes masquées (le détail affiche alors son propre état « Données
   * insuffisantes »). `subjectKey` est passée TELLE QUELLE : c'est un segment de chemin opaque, le
   * routeur l'encode ; aucune interprétation du préfixe (`need:` / `catalog:`) ici.
   */
  goToSubject(subjectKey: string): void {
    this.router.navigate(['/pilotage-prevention/besoins', subjectKey]);
  }

  // ───────────────────────── Formatage (jamais 0 / 0 % / valeur reconstruite pour un `null`) ─────────────────────────

  formatRate(rate: number | null | undefined): string {
    if (rate == null) return this.translate.instant('opportunites.states.insufficientData');
    return Math.round(rate * 100) + '%';
  }

  formatCount(value: number | null | undefined): string {
    if (value == null) return this.translate.instant('opportunites.states.insufficientData');
    return String(value);
  }

  /** Sous-texte de la cellule « Encore sans réponse » : `coverageRate` fourni par l'API, jamais recalculé (0,6 -> « 60 % »). Sans taux (rien de résolu), « Couverture non disponible ». */
  formatUncoveredCoverageNote(rate: number | null | undefined): string {
    if (rate == null) return this.translate.instant('opportunites.states.coverageUnavailable');
    const pct = Math.round(rate * 100);
    return this.translate.instant(
      pct === 0 ? 'opportunites.table.uncoveredCoverageZero' : 'opportunites.table.uncoveredCoverage',
      { rate: pct }
    );
  }

  formatDate(iso: string | null | undefined): string {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
  }
}
